import { useEffect, useMemo, useState } from "react";
import { PageHeader, SectionCard, Pill } from "@/components/ui-bits";
import { Download, FileText, FileSpreadsheet, FileType2, Filter, X } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import { useConstants, labelFor } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_FILTERS = {
  assessment_id: "",
  batch_id: "",
  assessment_type: "",
  trainee_query: "",
  result: "",
  date_from: "",
  date_to: "",
};

export default function Reports() {
  const constants = useConstants();
  const { user } = useAuth();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [assessments, setAssessments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [report, setReport] = useState({ rows: [], total: 0, passed: 0, failed: 0, avg_percentage: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/assessments").then((r) => setAssessments(r.data));
    api.get("/batches").then((r) => setBatches(r.data));
  }, []);

  const queryString = useMemo(() => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
    return q.toString();
  }, [filters]);

  const runReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/results?${queryString}`);
      setReport(data);
    } catch {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runReport(); /* eslint-disable-next-line */ }, [queryString]);

  const download = (format) => {
    const url = `${API_BASE}/reports/results/download?format=${format}&${queryString}`;
    window.location.href = url;
    toast.success(`${format.toUpperCase()} download started`);
  };

  const clearFilters = () => setFilters(DEFAULT_FILTERS);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  // Which formats does this role get?
  const formats = user?.role === "trainer" ? ["pdf", "xlsx"] : ["pdf", "xlsx", "csv"];

  return (
    <div>
      <PageHeader
        title="Assessment Reports"
        subtitle={`Filter assessment results and export as ${formats.map((f) => f.toUpperCase()).join(", ")}.`}
      />

      {/* Summary tiles */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SummaryTile label="Total rows"    value={report.total} tone="slate" />
        <SummaryTile label="Passed"        value={report.passed} tone="emerald" />
        <SummaryTile label="Failed"        value={report.failed} tone="red" />
        <SummaryTile label="Avg. score %"  value={`${report.avg_percentage}%`} tone="blue" />
      </div>

      {/* Filters */}
      <SectionCard title={
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-blue-600" />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters}
              className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600">
              <X className="w-3 h-3" /> Clear {activeFilterCount}
            </button>
          )}
        </div>
      }>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <FilterField label="Assessment">
            <select data-testid="filter-assessment" value={filters.assessment_id}
              onChange={(e) => setFilters({ ...filters, assessment_id: e.target.value })} className={input}>
              <option value="">All assessments</option>
              {assessments.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
            </select>
          </FilterField>
          <FilterField label="Assessment type">
            <select data-testid="filter-assessment-type" value={filters.assessment_type}
              onChange={(e) => setFilters({ ...filters, assessment_type: e.target.value })} className={input}>
              <option value="">All types</option>
              {(constants.assessment_types || []).map((t) =>
                <option key={t} value={t}>{labelFor("assessment_types", t)}</option>)}
            </select>
          </FilterField>
          <FilterField label="Batch / Team">
            <select data-testid="filter-batch" value={filters.batch_id}
              onChange={(e) => setFilters({ ...filters, batch_id: e.target.value })} className={input}>
              <option value="">All batches</option>
              {batches.map((b) => <option key={b.id} value={b.id}>{b.batch_name}</option>)}
            </select>
          </FilterField>
          <FilterField label="Result">
            <select data-testid="filter-result" value={filters.result}
              onChange={(e) => setFilters({ ...filters, result: e.target.value })} className={input}>
              <option value="">Pass & Fail</option>
              <option value="passed">Passed only</option>
              <option value="failed">Failed only</option>
            </select>
          </FilterField>
          <FilterField label="Trainee name / email / Employee ID">
            <input data-testid="filter-trainee" value={filters.trainee_query}
              onChange={(e) => setFilters({ ...filters, trainee_query: e.target.value })}
              placeholder="Search by name, email, EMP…" className={input} />
          </FilterField>
          <FilterField label="Date from">
            <input type="date" data-testid="filter-date-from" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className={input} />
          </FilterField>
          <FilterField label="Date to">
            <input type="date" data-testid="filter-date-to" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className={input} />
          </FilterField>
        </div>
      </SectionCard>

      {/* Download bar */}
      <div className="my-6 flex items-center justify-between flex-wrap gap-3 bg-white border border-slate-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Download className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-slate-900">Download report</span>
          <span className="text-slate-400">·</span>
          <span>{report.total} rows will be exported</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          {formats.includes("pdf") && (
            <button data-testid="download-pdf" onClick={() => download("pdf")}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-red-400 hover:bg-red-50 text-slate-700 rounded-lg px-3 py-2 text-sm font-medium">
              <FileType2 className="w-4 h-4 text-red-600" /> Download PDF
            </button>
          )}
          {formats.includes("xlsx") && (
            <button data-testid="download-xlsx" onClick={() => download("xlsx")}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-emerald-400 hover:bg-emerald-50 text-slate-700 rounded-lg px-3 py-2 text-sm font-medium">
              <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Download Excel
            </button>
          )}
          {formats.includes("csv") && (
            <button data-testid="download-csv" onClick={() => download("csv")}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-700 rounded-lg px-3 py-2 text-sm font-medium">
              <FileText className="w-4 h-4 text-blue-600" /> Download CSV
            </button>
          )}
        </div>
      </div>

      {/* Preview table */}
      <SectionCard title={`Results preview (${report.rows.length})`}>
        {loading ? (
          <div className="text-center text-sm text-slate-500 py-10">Loading…</div>
        ) : report.rows.length === 0 ? (
          <div className="text-center text-sm text-slate-500 py-10">No results match these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Employee</th>
                  <th className="px-4 py-3 font-medium">EMP ID</th>
                  <th className="px-4 py-3 font-medium">Batch</th>
                  <th className="px-4 py-3 font-medium">Assessment</th>
                  <th className="px-4 py-3 font-medium">Score</th>
                  <th className="px-4 py-3 font-medium">%</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.slice(0, 200).map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`report-row-${r.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{r.student_name}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono-acc text-xs">{r.employee_id || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.batch_name || "—"}</td>
                    <td className="px-4 py-3 text-slate-700">{r.assessment_title}</td>
                    <td className="px-4 py-3 text-slate-700">{r.score}/{r.total}</td>
                    <td className="px-4 py-3 font-medium">{r.percentage}%</td>
                    <td className="px-4 py-3">
                      <Pill tone={r.status === "passed" ? "green" : "red"}>{r.status}</Pill>
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{r.submitted_at?.slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {report.rows.length > 200 && (
              <div className="text-xs text-slate-500 text-center py-3">
                Showing first 200 rows in preview — the full {report.rows.length} rows will be exported.
              </div>
            )}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function SummaryTile({ label, value, tone }) {
  const tones = {
    slate: "bg-white border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    red: "bg-red-50 border-red-200 text-red-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
  };
  return (
    <div className={`border rounded-2xl p-5 ${tones[tone]}`}>
      <div className="text-xs uppercase tracking-wider opacity-70 font-medium">{label}</div>
      <div className="mt-1 text-2xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

const input = "w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none";
